import { useState, useEffect, useRef, useContext } from "react";
import { GraphNotCompatible } from "../../../components/GraphNotCompatible";
import { Chart } from "react-chartjs-2";
import { Chart as Chart2 } from "chart.js";
import { gql, useLazyQuery } from "@apollo/client";
import { Entorno } from "../../../utils/constants";
import {
  getItemHexColor,
  mergeKeyData,
  getMax,
  getMin,
  getSteps,
} from "../../../utils/functions";
import Chainz from "../../../dictionaries/Chainz.json";
import { Spinner, SpinnerSize } from "@fluentui/react";
import AppContext from "../../../AppContext";
import { getMedidaSQL } from "../../../utils/functions";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useTranslation } from "react-i18next";
import i18n from "../../../config/i18n";
import { t } from "i18next";
Chart2.register(ChartDataLabels);

export type IAnnotation = {
  label: string;
  type: IAnnotationType;
  value: number;
};
type IAnnotationType =
  | "mercado"
  | "especialidad"
  | "segmento"
  | "other"
  | "total"
  | "desglose1"
  | "desglose2";
export type IBarPropsData = {
  datalabels: string[];
  labels: string[];
  values: number[][];
  colors: string[];
  annotation: IAnnotation[];
  series: any;
  horizontal?: boolean;
  min?: number;
  max?: number;
  stepSize?: number;
};
export type IBarProps = {
  data: IBarPropsData;
  hue: number;
  annotation: string;
  percentages?: boolean;
};

const getUbicacionLabel = (key: string) => {
  if (key === "Total" || key === "U0") return "Ubicación Total";
  if (key === "CC" || key === "U3") return t('CcResto');
  if (key === "Resto" || key === "U4") return t('Resto');
  if (key === "Calle" || key === "U1") return t('Street');
  return key;
};

const getRegionLabel = (key: string, leverKeys: any) => {
  return leverKeys.Region[key] || key;
};

const getMomentoLabel = (key: string, leverKeys: any) => {
  const original = leverKeys.Momento[key] || key;
  return t(original);
};


const getLocationLabel = (key: string, isPT: boolean) => {
  if (key === "Local") return t("On-premises");
  // En Portugal, sólo existe "CcResto"
  if (isPT && key === "CcResto") return t("CcResto");
  // En España, existen "Cc" y "Resto" por separado
  if (!isPT && key === "CC") return t("Cc");
  if (!isPT && key === "Resto") return t("Resto");
  if (!isPT && key === "Calle") return t("Street");
  // El resto, usa la clave tal cual
  return t(key);
};

const renombrarCabecera = (nombre: string, isPT?: boolean) => {
  if (
    !isPT &&
    /((c c y resto)|(cc y resto)|(c c)|(cc))$/i.test(nombre.trim())
  ) {
    return nombre.replace(/((c c y resto)|(cc y resto)|(c c)|(cc))$/i, "CC");
  }
  if (
    isPT &&
    /((c c y resto)|(cc y resto)|(c c)|(cc))$/i.test(nombre.trim())
  ) {
    return nombre.replace(/((c c)|(cc))$/i, "CC y resto");
  }
  return nombre;
};

const esColumnaExcluida = (nombre: string) => {
  const excluidos = ["concesiones", "otros"];
  const lower = nombre.toLowerCase().trim();
  if (
    lower.endsWith("cc") ||
    lower.endsWith("cc y resto")
  ) return false;
  return excluidos.some((ex) => lower.endsWith(ex));
};

const getExportData = (
  data: any,
  datalabels: any[],
  labels: any,
  isLeap: boolean,
  currentFilters: any,
  leverTypeKeys: any,
  leverKeys: any,
  isPT: boolean 
) => {
  let exportData: any = [];
  let arrayDeCosas: any = [];
  if (currentFilters.entorno === 0) {
    arrayDeCosas.push("Totales");
  } else {
    arrayDeCosas.push("Comparables");
  }
  let test: any = Object.values(data[0]);
  let test2: any = test[0].map((item: any) => item?.Descripcion);

  for (let i = 0; i < test2.length; i++) {
    let tempVals: any = {};
    ["Ventas", "Tickets"].forEach((med: string) => {
      const medLabel = med === "Ventas" ? t("Sales") : t("Tickets");
      arrayDeCosas.forEach((ent: string) => {
        if (currentFilters.leverType === 0) {
          let tempVal = data
            .map((dat: any) =>
              Object.values(dat).map((d: any) => d[i]?.Item[med][ent])
            )
            .flat()
            .reverse();

          let payload: any = {};
          tempVal.map((itemTemp: any) => {
            Object.keys(itemTemp).map((key: any) => {
              if (!payload[key]) {
                payload[key] = [itemTemp[key]];
              } else {
                payload[key].push(itemTemp[key]);
              }
            });
          });

          Object.keys(payload).map((key: any) => {
            if (isPT && key === "Resto") return;
            let exportKey =
              currentFilters.leverType === 4
                ? getLocationLabel(key, isPT)
                : key;
            const nombreColumna = renombrarCabecera(medLabel + " " + exportKey, isPT);
            if (!esColumnaExcluida(nombreColumna)) {
              tempVals[nombreColumna] = {
                ...mergeKeyData(labels, payload[key]),
              };
            }
          });

        } else {
          let palanca = leverTypeKeys[currentFilters.leverType];
          let subPalanca: any = Object.values(leverKeys[palanca])[currentFilters.lever];

          let tempVal = data
            .map((dat: any) =>
              Object.values(dat).map((d: any) => d[i]?.Item[med][ent][palanca])
            )
            .flat()
            .reverse();

          let payload: any = {};
          tempVal.map((itemTemp: any) => {
            Object.keys(itemTemp).map((key: any) => {
              if (!payload[key]) {
                payload[key] = [itemTemp[key]];
              } else {
                payload[key].push(itemTemp[key]);
              }
            });
          });

          Object.keys(payload).map((key: any) => {
            if (isPT && key === "Resto") return;
            let exportKey =
              currentFilters.leverType === 4
                ? getLocationLabel(key, isPT)
                : currentFilters.leverType === 3
                ? getRegionLabel(key, leverKeys)
                : currentFilters.leverType === 2
                ? getMomentoLabel(key, leverKeys)
                : key;
            // Usa medLabel traducido aquí
            const nombreColumna = renombrarCabecera(medLabel + " " + exportKey, isPT);
            if (!esColumnaExcluida(nombreColumna)) {
              tempVals[nombreColumna] = {
                ...mergeKeyData(labels, payload[key]),
              };
            }
          });
        }
      });
    });
    exportData.push({ [test2[i]]: tempVals });
  }

  let exportDataFiltered = exportData.filter((item: any) => {
    let key = Object.keys(item)[0];
    return datalabels.includes(key);
  });

  return exportDataFiltered;
};

export const ComparadorGap = ({
  variables,
  hue,
  annotation,
  percentages,
  legend,
  updateLegend,
  updateExportData,
  showLegend,
}: any) => {
  let statusRef = useRef<any>([]);
  const graphRef = useRef<any>(null);
  let appContext = useContext(AppContext);
  const { semanas, filters, user } = useContext(AppContext);

  let currentFilters = useRef<any>(null);

  let leverTypeKeys: any = [
    "Totales",
    "Canal",
    "Momento",
    "Region",
    "Ubicacion",
  ];
  const isPT =
    typeof window !== "undefined" &&
    (window.location.host.includes("pt"));
  let leverKeys: any = {
    Canal: { C0: "Total", C1: "Local", C2: "TakeAway", C3: "Delivery" },
    Momento: {
      M0: "Total",
      M1: "Mañanas",
      M2: "Tardes",
      M3: "Comidas (L-J)",
      M4: "Cenas (L-J)",
      M5: "Comidas (V-D)",
      M6: "Cenas (V-D)",
    },
    Region: isPT
      ? {
          Z0: "Total",
          Z1: "Lisboa",
          Z2: "Porto",
          Z3: t('North'),
          Z4: t('Center'),
          Z5: "Alenteho",
          Z6: "Algarve",
        }
      : {
          Z0: "Total",
          Z1: "Madrid",
          Z2: "Barcelona",
          Z3: t('North'),
          Z4: t('Center'),
          Z5: t('Northeast'),
          Z6: t('South'),
        },
    Ubicacion: isPT
      ? { U0: "Total", U1: "Calle", U3: "CcResto" }
      : { U0: "Total", U1: "Calle", U3: "CC", U4: "Resto" }
  };

  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $wk1: Int!
      $yr2: Int!
      $wk2: Int!
      $var: Int
      $segment: String
      $segmentType: Int
      $misdatos: Int
    ) {
      graph(
        Graph: "GAP comparador"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: {
          Variables: $var
          Segmento: $segment
          SegmentType: $segmentType
          misdatos: $misdatos
        }
      )
    }
  `;

  const [graphData, setGraphData] = useState<any>(null);
  let emptyData = {
    labels: [],
    datasets: [],
  };
  const [errorMsg, setErrorMsg] = useState<string>(
    `Se ha producido un error recuperando los datos`
  );

  const [isCompatible, setCompatible] = useState<boolean>(true);

  const [fetchData, { loading, error, data }] = useLazyQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    fetchData({
      variables: {
        ...variables,
        yr1: semanas[0].year,
        wk1: semanas[0].week,
        yr2: semanas[1].year,
        wk2: semanas[1].week,
        var: 1,
        segment:
          filters.segmentType === 0
            ? filters?.segmentos[filters?.segment]
            : filters?.especialidades[filters?.segment],
        segmentType: filters.segmentType,
        misdatos: filters?.misdatos,
      },
    });
    currentFilters.current = { ...filters };
  }, [semanas, filters?.segmentType, filters?.segment, filters?.misdatos]);

  useEffect(() => {
    if (data?.graph && !loading) {
      if (data?.graph?.Error) {
        setCompatible(false);
        setErrorMsg(data?.graph?.Error);
        return;
      }

      setCompatible(true);

      const medida = getMedidaSQL(filters.medidas);
      let entorno =
        filters.entorno === Entorno.Comparable ? "Comparables" : "Totales";

      let fetchedData: any = data?.graph;

      if (!fetchedData) throw new Error(errorMsg);
      if (fetchedData?.Error) throw new Error(`${data.Error}`);

      let labels = fetchedData?.map((d: Record<string, unknown>) => Object.keys(d)[0]);
      let datalabels = fetchedData[0][labels[0]].map(
        (d: { Descripcion: string }) => d.Descripcion
      );
      let dataName = fetchedData[0][labels[0]].map(
        (d: { Name: string }) => d.Name
      );
      let values: any = [];
      let series: any = [];
      let values2: any = {};

      let segmentsArray: any = [];
      if (filters.variables === 0) {
        segmentsArray = datalabels.filter(
          (item: string) => item?.indexOf("sin m") > -1
        );
      } else {
        segmentsArray = datalabels.filter(
          (item: string) =>
            item?.substring(0, 4) ===
            filters.segmentos[filters.variables].substring(0, 4)
        );
      }

      let myDataArray: any = [];
      let cadenas = datalabels.filter(
        (item: string) => item?.substring(0, 4) === "cad_"
      );
      myDataArray = [...cadenas];

      let filteredLabelsInitial: any = [];
      segmentsArray.map((item: string) => filteredLabelsInitial.push(item));
      myDataArray.map((item: string) => filteredLabelsInitial.push(item));

      values2 = filteredLabelsInitial.map((item: string) => {
        return { [item]: [] };
      });

      try {
        for (let i = 0; i < filteredLabelsInitial.length; i++) {
          let valueSub: any = [];
          fetchedData.map((dat: any): void => {
            let tempArray: any = [...Object.values(dat)];
            let index = tempArray[0].findIndex((item2: any) => {
              return (
                typeof item2["Descripcion"] === "string" &&
                item2["Descripcion"].substring(0, 7) ===
                  filteredLabelsInitial[i].substring(0, 7)
              );
            });

            let arrayOfItems = tempArray[0][index];
            let valueSub2 = arrayOfItems?.Item[medida][entorno];
            let index2 = values2.findIndex((item2: any) => {
              return (
                Object.keys(item2)[0].substring(0, 7) ===
                filteredLabelsInitial[i].substring(0, 7)
              );
            });
            values2[index2][filteredLabelsInitial[i]].push(valueSub2);

            if (filters.leverType === 0) {
              valueSub.push(arrayOfItems?.Item[medida][entorno].Totales);
            } else {
              let leverKey: string = leverTypeKeys[filters.leverType || 0];
              let leverSubKey =
                leverKeys[leverKey as keyof typeof leverKeys][
                  filters.lever || 0
                ];
              valueSub.push(
                arrayOfItems?.Item[medida][entorno][leverKey][leverSubKey]
              );
            }
          });
          values.push(valueSub.flat().reverse());
        }
      } catch {
        setCompatible(false);
      }

      const ubiLabel = (label: string) => getUbicacionLabel(label);
      const validIdx: number[] = labels.map((lbl: string) => ubiLabel(lbl))
        .map((lbl: string, idx: number) => ({ lbl, idx }))
        .map(({ idx }: { idx: number }) => idx);

      const finalLabels = validIdx.map(idx => labels[idx]);
      const finalDatalabels = validIdx.map(idx => datalabels[idx]);
      const finalDataNames = validIdx.map(idx => dataName[idx]);
      const finalValues = validIdx.map(idx => values[idx]);
      const finalSeries = validIdx.map((idx, i) => {
        let color = "#ffffff";
        const chainIndex = Object.keys(Chainz).findIndex(chainItem => chainItem === finalDataNames[i]);
        if (chainIndex !== -1) {
          color = Chainz[finalDataNames[i] as keyof typeof Chainz].color;
        }
        return {
          name: ubiLabel(finalLabels[i]),
          color,
          data: finalValues[i],
        };
      });

      setGraphData({
        datalabels: finalDatalabels,
        labels: finalLabels,
        values: finalValues,
        series: finalSeries,
        values2: values2,
      });

      if (updateExportData) {
        currentFilters.current = { ...filters };
        let exportData = getExportData(
          fetchedData,
          finalDatalabels,
          finalLabels,
          semanas[0].year === 2019,
          currentFilters.current,
          leverTypeKeys,
          leverKeys,
          isPT
        );
        updateExportData(exportData);
      }

      return;
    }
  }, [data, filters, errorMsg]);

  useEffect(() => {
    //@ts-ignore -- Limitación de apexcharts?
    if (graphRef.current && graphRef.current.props.series.length > 5) {
      graphData?.series.forEach(
        (serie: { name: string; data: number[] }, i: number) => {
          if (i > 5) graphRef?.current?.chart.hideSeries(serie.name);
        }
      );
    }
  }, [graphData]);

  const newLegendClickHandler = function (
    e: any,
    legendItem: any,
    legend: any
  ) {
    const index = legendItem.datasetIndex;
    const ci = legend.chart;

    if (ci.isDatasetVisible(index)) {
      ci.hide(index);
      legendItem.hidden = true;
    } else {
      ci.show(index);
      legendItem.hidden = false;
    }
    ci.update();

    let status = statusRef.current;
    if (status) {
      status[index].hidden = !status[index].hidden;
      // updateLegend(status);
    }
  };

  const newLegendHoverHandler = function (
    e: any,
    legendItem: any,
    legend: any
  ) {
    const index = legendItem?.datasetIndex;
    const ci = legend.chart;
    setHoveredIndex(index);
    ci.update();
  };

  const newLegendLeaveHandler = function (
    e: any,
    legendItem: any,
    legend: any
  ) {
    const ci = legend.chart;
    setHoveredIndex(-1);
    ci.update();
  };

  const plugin: any = {
    id: "verticalLiner",
    afterInit: (chart: any, args: any, opts: any) => {
      chart.verticalLiner = {};
    },
    afterEvent: (chart: any, args: any, options: any) => {
      const { inChartArea } = args;
      chart.verticalLiner = { draw: inChartArea };
    },
    beforeTooltipDraw: (chart: any, args: any, options: any) => {
      const { draw } = chart.verticalLiner;
      if (!draw) return;

      const { ctx } = chart;
      const { top, bottom } = chart.chartArea;
      const { tooltip } = args;
      const x = tooltip?.caretX;
      if (!x) return;

      ctx.save();

      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
      ctx.strokeStyle = "#545455";
      ctx.stroke();

      ctx.restore();
    },
  };

  const options = {
    responsive: true,
    tension: 0,
    animation: {
      duration: 400,
    },
    interaction: {
      intersect: false,
      mode: "index" as any,
    },
    plugins: {
      verticalLiner: {},
      datalabels: {
        color: showLegend ? "white" : "transparent",
      },
      legend: {
        reverse: false,
        position: "bottom" as const,
        onClick: newLegendClickHandler,
        onHover: newLegendHoverHandler,
        onLeave: newLegendLeaveHandler,
        labels: {
          color: "rgb(255, 255, 255, 0.9)",
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      title: {
        display: false,
        text: "Mi tendencia",
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: true,
          drawBorder: false,
          drawOnChartArea: true,
          drawTicks: true,
          tickColor: "#26292A",
          color: "#26292A",
        },
        ticks: {
          callback: function (value: any) {
            return value.toFixed(0); 
          },
        },
      },
    },
  };

  let [dataTest, setDataTest] = useState<any>({
    labels: [],
    datasets: [],
  });

  let [hoveredIndex, setHoveredIndex] = useState<number>(-1);

  useEffect(() => {
    if (
      !graphData ||
      graphData?.labels?.length === 0 ||
      graphData?.series?.length === 0
    )
      return;

    let datasets: any = [];
    let segmentsCount: number = 0;
    let chainsCount: number = 0;
    let graphDataGrouped: any = [];
    let datasetLabels: any = [];
    graphData.values2.map((item: any) => {
      let keys = Object.keys(item);
      graphDataGrouped.push({
        [keys[0]]: [],
      });
    });

    if (filters.leverType === 0) {
      graphData.values2.forEach((item: any, index: number) => {
        let key = Object.keys(item)[0];
        datasetLabels.push(key);
        let collection = item[key];

        let collectionKeys = Object.keys(collection[0]);
        collectionKeys = ["Totales"];
        datasetLabels = [...collectionKeys];

        let subseries: any = [];

        collectionKeys.forEach((key: any) => {
          let newSubserie = {
            name: key,
            data: collection
              .map((item: any) => item[key].Total || item?.Totales)
              .reverse()
              .flat(),
            color: "#ffffff",
          };
          subseries.push(newSubserie);
        });

        let currentDataSet: any = [];
        subseries.forEach(
          (
            serie: {
              name: string;
              data: number[];
              color: string;
              labels: string[];
            },
            index: number
          ) => {
            let color: string = serie.color;
            color = getItemHexColor("comparator", index);

            currentDataSet.push({
              type: "line",
              order: "1",
              label: getLocationLabel(serie.name, isPT),
              data: serie.data,
              borderColor:
                hoveredIndex !== index && hoveredIndex !== -1
                  ? "rgba(255,255,255,0.10"
                  : color,
              backgroundColor:
                hoveredIndex !== index && hoveredIndex !== -1
                  ? "rgba(255,255,255,0.10"
                  : color,
              hidden: statusRef?.current[index]?.hidden || false,
              fill: {
                target: "origin",
                above: color,
                below: color,
              },
            });
          }
        );

        let data4: any = {
          labels: [...graphData.labels].reverse(),
          datasets: currentDataSet,
        };

        setDataTest(data4);
      });
    } else {
      let subLever = leverTypeKeys[filters.leverType || 0];

      graphData.values2.forEach((item: any, index: number) => {
        let key = Object.keys(item)[0];
        datasetLabels.push(key);
        let collection = item[key];

        let collectionKeys = Object.keys(collection[0][subLever]);
        collectionKeys = collectionKeys.filter((item: any) => item !== "Total");
        datasetLabels = [...collectionKeys];

        let subseries: any = [];

        collectionKeys.forEach((key: any) => {
          if (
            key !== "Totales" &&
            key !== "Concesiones" &&
            key !== "Otros" &&
            !(filters.leverType === 4 && isPT && key === "Resto")
          ) {
            let newSubserie = {
              name:
                filters.leverType === 3
                  ? getRegionLabel(key, leverKeys)
                  : key,
              data: collection
                .map((item: any) => item[subLever][key])
                .reverse()
                .flat(),
              color: "#ffffff",
            };

            if (filters.lever === 0) {
              subseries.push(newSubserie);
            } else {
              let subkeys: any =
                Object.values(leverKeys)[filters.leverType - 1];

              if (
                subkeys[Object.keys(subkeys)[filters.lever]] ===
                newSubserie.name
              ) {
                subseries.push(newSubserie);
              }
            }
          }
        });

        let currentDataSet: any = [];

        subseries.forEach(
          (
            serie: {
              name: string;
              data: number[];
              color: string;
              labels: string[];
            },
            index: number
          ) => {
            let color: string = serie.color;
            color = getItemHexColor("comparator", index);

            currentDataSet.push({
              type: "line",
              order: "1",
              label: filters.leverType === 4
                ? getLocationLabel(serie.name, isPT)
                : filters.leverType === 3
                ? getRegionLabel(serie.name, leverKeys)
                : filters.leverType === 2
                ? getMomentoLabel(serie.name, leverKeys)
                : filters.leverType === 1
                ? (serie.name === "Local" ? t("On-premises") : t(serie.name))
                : serie.name,
              data: serie.data,
              borderColor:
                hoveredIndex !== index && hoveredIndex !== -1
                  ? "rgba(255,255,255,0.10"
                  : color,
              backgroundColor:
                hoveredIndex !== index && hoveredIndex !== -1
                  ? "rgba(255,255,255,0.10"
                  : color,
              hidden: statusRef?.current[index]?.hidden || false,
              fill: {
                target: "origin",
                above: color,
                below: color,
              },
            });
          }
        );

        let data4: any = {
          labels: [...graphData.labels].reverse(),
          datasets: currentDataSet,
        };

        setDataTest(data4);
      });
    }

    if (statusRef.current.length === 0) {
      // statusRef.current = datasets;
    }
  }, [graphData, hoveredIndex, legend]);

  return (
    <div className="chart-container">
      {dataTest && (
        <div className="chart-wrapper">
          <Chart
            type="bar"
            data={!loading ? dataTest : emptyData}
            options={options}
            plugins={[plugin]}
          />

          {!isCompatible && !loading && (
            <GraphNotCompatible>{errorMsg}</GraphNotCompatible>
          )}
          {isCompatible && filters.leverType === 10 && !loading && (
            <GraphNotCompatible>
              {`Seleccione la variable a observar en el panel de filtros.`}
            </GraphNotCompatible>
          )}
          {filters.entorno === Entorno.Comparable &&
            filters.leverType > 0 &&
            !loading && (
              <GraphNotCompatible>
                {t('Graph-not-support')}
              </GraphNotCompatible>
            )}
        </div>
      )}

      {loading && (
        <div className="loading-graph">
          <Spinner size={SpinnerSize.large} />
          <p>{t('Extracting-data')}</p>
        </div>
      )}
    </div>
  );
};
